import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ExpandLess,
  ExpandMore,
  Cancel as CancelAllIcon,
  Refresh as RetryIcon,
  Clear as ClearIcon,
  CloudQueue as QueueIcon
} from '@mui/icons-material';
import { UploadProgress, UploadProgress as UploadProgressComponent } from './UploadProgress';
import { en } from '../translations/en';

interface UploadQueueProps {
  uploadQueue: UploadProgress[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onCancelAll: () => void;
  onRetryFailed: () => void;
  onClearCompleted: () => void;
  onCancel: (fileId: string) => void;
  onRetry: (fileId: string) => void;
  onRemove: (fileId: string) => void;
  'data-testid'?: string;
}

const getQueueStats = (queue: UploadProgress[]) => {
  const stats = {
    total: queue.length,
    pending: 0,
    uploading: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    cancelled: 0
  };

  queue.forEach(item => {
    stats[item.status as keyof typeof stats]++;
  });

  return stats;
};

export const UploadQueue: React.FC<UploadQueueProps> = ({
  uploadQueue,
  collapsed = false,
  onToggleCollapse,
  onCancelAll,
  onRetryFailed,
  onClearCompleted,
  onCancel,
  onRetry,
  onRemove,
  'data-testid': testId
}) => {
  const theme = useTheme();
  const stats = getQueueStats(uploadQueue);

  const hasActiveUploads = stats.uploading > 0 || stats.pending > 0 || stats.processing > 0;
  const hasFailedUploads = stats.failed > 0;
  const hasCompletedUploads = stats.completed > 0 || stats.processing > 0;

  if (uploadQueue.length === 0) {
    return (
      <Card variant="outlined" sx={{ border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <QueueIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {en.uploadQueue.noUploads}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {en.uploadQueue.selectFiles}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" data-testid={testId} sx={{ border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <QueueIcon color="primary" />
            <Typography variant="h6">
              {en.uploadQueue.title}
            </Typography>
            <Chip
              label={`${stats.total} ${en.uploadQueue.files}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: theme.palette.primary.main }}
            />
          </Box>

          {onToggleCollapse && (
            <IconButton
              onClick={onToggleCollapse}
              aria-label={collapsed ? en.uploadQueue.expandQueue : en.uploadQueue.collapseQueue}
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              {collapsed ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {stats.uploading > 0 && (
            <Chip
              label={`${stats.uploading} ${en.uploadQueue.uploading}`}
              color="primary"
              size="small"
            />
          )}
          {stats.processing > 0 && (
            <Chip
              label={`${stats.processing} ${en.uploadQueue.processing}`}
              color="info"
              size="small"
            />
          )}
          {stats.completed > 0 && (
            <Chip
              label={`${stats.completed} ${en.uploadQueue.completed}`}
              color="success"
              size="small"
            />
          )}
          {stats.failed > 0 && (
            <Chip
              label={`${stats.failed} ${en.uploadQueue.failed}`}
              color="error"
              size="small"
            />
          )}
          {stats.pending > 0 && (
            <Chip
              label={`${stats.pending} ${en.uploadQueue.pending}`}
              color="default"
              size="small"
            />
          )}
          {stats.cancelled > 0 && (
            <Chip
              label={`${stats.cancelled} ${en.uploadQueue.cancelled}`}
              color="default"
              size="small"
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {hasActiveUploads && (
            <Button
              size="small"
              startIcon={<CancelAllIcon />}
              onClick={onCancelAll}
              color="error"
              variant="outlined"
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.error.light + '20'
                }
              }}
            >
              {en.uploadQueue.cancelAll}
            </Button>
          )}

          {hasFailedUploads && (
            <Button
              size="small"
              startIcon={<RetryIcon />}
              onClick={onRetryFailed}
              color="primary"
              variant="outlined"
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.primary.light + '20'
                }
              }}
            >
              {en.uploadQueue.retryFailed}
            </Button>
          )}

          {hasCompletedUploads && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={onClearCompleted}
              color="default"
              variant="outlined"
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              {en.uploadQueue.clearCompleted}
            </Button>
          )}
        </Stack>

        <Divider />
      </CardContent>

      {!collapsed && (
        <CardContent sx={{ pt: 0 }}>
          <Stack spacing={1}>
            {uploadQueue.map((progress) => (
              <UploadProgressComponent
                key={progress.fileId}
                progress={progress}
                onCancel={onCancel}
                onRetry={onRetry}
                onRemove={onRemove}
              />
            ))}
          </Stack>
        </CardContent>
      )}
    </Card>
  );
};
